import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { translate } from "../i18n/translate";
import { formatDate, formatMoney } from "../lib/format";
import {
  detectCurrency,
  detectLanguage,
  defaultCurrencyForLanguage,
  saveCurrency,
  saveLanguage,
  type AppCurrency,
  type AppLanguage,
} from "../lib/localePrefs";

interface LocaleContextValue {
  language: AppLanguage;
  currency: AppCurrency;
  setLanguage: (lang: AppLanguage) => void;
  setCurrency: (currency: AppCurrency) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  formatMoney: (amount: number) => string;
  formatDate: (input: string | Date | undefined) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() =>
    detectLanguage(),
  );
  const [currency, setCurrencyState] = useState<AppCurrency>(() =>
    detectCurrency(detectLanguage()),
  );

  useEffect(() => {
    document.documentElement.lang = language;
    const title = translate(language, "meta.title");
    if (title !== "meta.title") document.title = title;
  }, [language]);

  const setLanguage = useCallback((lang: AppLanguage) => {
    saveLanguage(lang);
    setLanguageState(lang);
    if (!localStorage.getItem("dwik-currency")) {
      const next = defaultCurrencyForLanguage(lang);
      setCurrencyState(next);
    }
  }, []);

  const setCurrency = useCallback((next: AppCurrency) => {
    saveCurrency(next);
    setCurrencyState(next);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) =>
      translate(language, key, vars),
    [language],
  );

  const formatMoneyFn = useCallback(
    (amount: number) => formatMoney(amount, language, currency),
    [language, currency],
  );

  const formatDateFn = useCallback(
    (input: string | Date | undefined) => formatDate(input, language),
    [language],
  );

  const value = useMemo(
    () => ({
      language,
      currency,
      setLanguage,
      setCurrency,
      t,
      formatMoney: formatMoneyFn,
      formatDate: formatDateFn,
    }),
    [
      language,
      currency,
      setLanguage,
      setCurrency,
      t,
      formatMoneyFn,
      formatDateFn,
    ],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
